import React from "react";
import useCurrentVehicleData from "../hooks/useCurrentVehicleData";
import BarChart from "./BarChart";
import ResultNeutral from "./ResultNeutral";
import {
    FUEL_LABELS,
    formatCLP,
    formatDecimal,
    priceUnitShort,
    savingsIsPositive,
} from "../utils/format";

const versionLabel = (v) => `${v.model_name} ${v.version_name}`.trim();

const ResultSingle = ({ version, entry }) => {
    const { getFuelType } = useCurrentVehicleData();

    // `!entry` es la ventana entre que los inputs quedan completos y que el
    // cálculo (debounced) sale: todavía no hay error, hay un POST en camino.
    if (!entry || (!entry.result && !entry.error)) {
        return <div className="savings-calculator__result-loading">Calculando…</div>;
    }
    if (entry.error) {
        return (
            <div className="savings-calculator__result-error">
                No pudimos calcular el ahorro. Revisa los datos ingresados.
            </div>
        );
    }

    const r = entry.result;
    const savings = parseFloat(r.annual_savings_clp);
    const positive = savingsIsPositive(savings);
    const userFuel = getFuelType();

    // Con ahorro positivo funciona bien el diseño de siempre; cuando no lo hay
    // se pasa a la variante neutra (sin cifras en rojo ni "pérdida").
    if (!positive) {
        return <ResultNeutral version={version} result={r} />;
    }

    const toyotaUnit = priceUnitShort(version.fuel_type);
    const userUnit = priceUnitShort(userFuel);

    return (
        <div className="savings-calculator__result">
            <p className="savings-calculator__result-eyebrow">
                Ahorro anual estimado vs. tu vehículo actual
            </p>
            <p className="savings-calculator__result-figure is-positive">
                {formatCLP(savings, { withSign: true })}
                <span className="savings-calculator__result-per savings-calculator__result-per--inline">
                    / año
                </span>
            </p>
            <p className="savings-calculator__result-message">
                Ahorro estimado en combustible según el rendimiento, precio y
                kilometraje que ingresaste.
            </p>

            <hr className="savings-calculator__result-divider" />

            <p className="savings-calculator__result-subtitle">Gasto anual en combustible</p>
            <p className="savings-calculator__result-caption">
                Comparación para {formatDecimal(r.assumed_annual_km, 0)} km al año
            </p>

            <BarChart
                bars={[
                    {
                        value: parseFloat(r.annual_cost_toyota_clp),
                        color: "toyota",
                        label: versionLabel(version),
                        sublabel: `${FUEL_LABELS[version.fuel_type]} · $${formatDecimal(
                            r.fuel_price_toyota_clp,
                            0
                        )}/${toyotaUnit}`,
                    },
                    {
                        value: parseFloat(r.annual_cost_current_clp),
                        color: "current",
                        label: "Tu vehículo",
                        sublabel: `${FUEL_LABELS[userFuel]} · $${formatDecimal(
                            r.fuel_price_current_clp,
                            0
                        )}/${userUnit}`,
                    },
                ]}
            />

            <dl className="savings-calculator__breakdown">
                <div>
                    <dt>Gasto {version.model_name} / año</dt>
                    <dd>{formatCLP(r.annual_cost_toyota_clp)}</dd>
                </div>
                <div>
                    <dt>Gasto actual / año</dt>
                    <dd>{formatCLP(r.annual_cost_current_clp)}</dd>
                </div>
                <div>
                    <dt>Ahorro mensual est.</dt>
                    <dd>{formatCLP(savings / 12)}</dd>
                </div>
                <div className="savings-calculator__breakdown-total">
                    <dt>Ahorro en 5 años</dt>
                    <dd className="is-positive">{formatCLP(savings * 5)}</dd>
                </div>
            </dl>
        </div>
    );
};

export default ResultSingle;
